import React, { useContext } from "react";
import { ObjectInfoPanelTabProps } from "./types";
import {
  SubmissionIntakeSource,
  useGetSubmissionsForCustomMapGeometryQuery,
} from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { CreateSubmissionButton } from "../Common/CreateSubmissionButton";
import { intersection, lowerCase, map } from "lodash";
import { EmptyState } from "../Common/EmptyState";
import { canCreateInternalSubmission } from "common/authorization/customPolicies";
import { FlexRow } from "../Common/__styles__/Layout";
import { Title } from "../Common/Typography";
import ListItem from "../AddressPanel/Submissions/ListItem";
import {
  ContentWrapper,
  TabContainer,
} from "./__styles__/ObjectInformationPanel";
import TabGroup from "../Common/TabGroup";
import useNavigationTab from "../../hooks/useNavigationTabs";
import { OBJECT_TYPE, SUBMISSION_CATEGORY } from "common/constants";
import { createSubmissionRedirect } from "../Common/CreateSubmissionButton/utils";
import { useHistory } from "react-router";

export const Layout = ({
  actionButton,
  children,
  title,
}: {
  actionButton?: React.ReactNode;
  children: React.ReactNode;
  title: string;
}) => (
  <div>
    <FlexRow style={{ justifyContent: "space-between" }}>
      <Title size="small" type="regular">
        <span>{title}</span>
      </Title>
      {actionButton}
    </FlexRow>
    {children}
  </div>
);

const SubmissionCategoryDisplay = ({
  customMap,
  geometry,
  title,
  category,
}: ObjectInfoPanelTabProps & {
  category: SUBMISSION_CATEGORY;
  title: string;
}) => {
  const { user, admin } = useContext(AuthContext);
  const history = useHistory();

  const { data, loading, error, refetch } =
    useGetSubmissionsForCustomMapGeometryQuery({
      variables: { geometryId: geometry.id, category },
      fetchPolicy: "cache-and-network",
    });

  if (loading) {
    return <em>Loading...</em>;
  }

  if (error) {
    return <span>Error loading {lowerCase(title)}</span>;
  }

  const currentUser = user || admin;
  const canCreateSubmission = canCreateInternalSubmission({
    role: currentUser?.role.name,
  });

  const actionButton = (
    <CreateSubmissionButton
      disabled={!canCreateSubmission}
      objectDisplay={geometry.label}
      onSubmit={submissionTypeId =>
        createSubmissionRedirect({
          objectId: geometry.id,
          objectType: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
          submissionTypeId,
          history,
        })
      }
      submissionTypeFilter={submissionType =>
        submissionType.category === category &&
        submissionType.intakeSource === SubmissionIntakeSource.INTERNAL &&
        submissionType.attachments.filter(
          attachment =>
            attachment.type === OBJECT_TYPE.CUSTOM_MAP_GEOMETRY &&
            intersection(attachment.tags, customMap.tags).length > 0
        ).length > 0
      }
    />
  );

  const submissions = data?.customMapGeometry?.submissions;
  if (!submissions?.length) {
    return (
      <Layout title={title} actionButton={actionButton}>
        <EmptyState
          icon="add-file"
          title={`No ${lowerCase(title)} for this object`}
          body={`Add ${lowerCase(title)} to get started.`}
        />
      </Layout>
    );
  }

  return (
    <Layout title={title} actionButton={actionButton}>
      {submissions.map((submission: any) => (
        <ListItem
          key={submission.id}
          submission={submission}
          onUpdate={refetch}
        />
      ))}
    </Layout>
  );
};

const Records = ({
  geometry,
  customMap,
  submissionCategories,
}: ObjectInfoPanelTabProps) => {
  if (!submissionCategories) {
    return null;
  }

  const tabs = [
    { value: SUBMISSION_CATEGORY.INSPECTIONS, label: "Inspections" },
    { value: SUBMISSION_CATEGORY.PERMITTING, label: "Permits" },
    { value: SUBMISSION_CATEGORY.GRANTS, label: "Grants" },
    { value: SUBMISSION_CATEGORY.PAYMENTS, label: "Payments" },
    { value: SUBMISSION_CATEGORY.MAILINGS, label: "Mailings" },
    { value: SUBMISSION_CATEGORY.CODE_CASES, label: "Code Cases" },
    { value: SUBMISSION_CATEGORY.PROJECTS, label: "Projects" },
    { value: SUBMISSION_CATEGORY.PLANNING, label: "Planning" },
    { value: SUBMISSION_CATEGORY.INSURANCE, label: "Insurance" },
  ].filter(tab => submissionCategories.has(tab.value));

  const [tab, setTab] = useNavigationTab({
    defaultTab: tabs[0]?.value ?? SUBMISSION_CATEGORY.INSPECTIONS,
    allowedTabs: map(tabs, "value"),
    queryParamName: "recordTab",
  });

  const currentTab = tabs.find(({ value }) => value === tab);

  return (
    <>
      <TabContainer style={{ marginTop: "8px" }}>
        <TabGroup currentTab={tab} setTab={setTab} tabs={tabs} />
      </TabContainer>
      {currentTab && (
        <ContentWrapper>
          <SubmissionCategoryDisplay
            customMap={customMap}
            geometry={geometry}
            title={currentTab.label}
            category={currentTab.value}
          />
        </ContentWrapper>
      )}
    </>
  );
};

export default Records;
